import { useShallow } from 'zustand/react/shallow';

import { isAddMessageEnabled } from '@/feature/message.feature';
import { useMessagesStore } from '@/store/messages.store';
import Tabs, { Tab, TabPanel } from '@ingka/tabs';
import { translate } from '@repo/phrase';
import { MessageTabs, isMessageTab } from '@repo/types/messages-tabs';
import { AddMessage, MessageBanner, type MessageBannerState } from '@repo/ui';
import type { MutationStatus } from '@tanstack/react-query';

import { useAssetParams } from '@/hooks/asset.hooks';

import { AssetMessageModalAddContent } from './asset-message-modal-add-content/asset-message-modal-add-content.component';

interface Props {
    bannerState: MessageBannerState;
    setBannerState: (bannerState: MessageBannerState) => void;
    addMessageStatus: MutationStatus;
    onAdd: (message: string) => Promise<boolean>;
}
export const AssetMessageModalAdd = ({ bannerState, setBannerState, addMessageStatus, onAdd }: Props) => {
    const { assetType } = useAssetParams();
    const { activeTab, setActiveTab } = useMessagesStore(
        useShallow((state) => ({
            activeTab: state.activeTab,
            setActiveTab: state.setActiveTab,
        }))
    );

    const tabs = [
        <Tab
            key={MessageTabs.TSP_CUST}
            text={translate('messages_modal_customer')}
            tabPanelId={MessageTabs.TSP_CUST}
        />,
        <Tab
            key={MessageTabs.INTERNAL}
            text={translate('messages_modal_internal')}
            tabPanelId={MessageTabs.INTERNAL}
        />,
        <Tab
            key={MessageTabs.EXTERNAL}
            text={translate('messages_modal_transport')}
            tabPanelId={MessageTabs.EXTERNAL}
        />,
    ];

    const tabPanels = [
        <TabPanel key={MessageTabs.TSP_CUST} tabPanelId={MessageTabs.TSP_CUST}>
            {isAddMessageEnabled(assetType, MessageTabs.TSP_CUST) && (
                <AddMessage onAdd={onAdd} translate={translate} isLoading={addMessageStatus === 'pending'} />
            )}
        </TabPanel>,
        <TabPanel key={MessageTabs.INTERNAL} tabPanelId={MessageTabs.INTERNAL}>
            {isAddMessageEnabled(assetType, MessageTabs.INTERNAL) && (
                <AddMessage onAdd={onAdd} translate={translate} isLoading={addMessageStatus === 'pending'} />
            )}
        </TabPanel>,
        <TabPanel key={MessageTabs.EXTERNAL} tabPanelId={MessageTabs.EXTERNAL}>
            {isAddMessageEnabled(assetType, MessageTabs.EXTERNAL) && (
                <AddMessage onAdd={onAdd} translate={translate} isLoading={addMessageStatus === 'pending'} />
            )}
        </TabPanel>,
    ];

    return (
        <>
            <Tabs
                tabs={tabs}
                tabPanels={tabPanels}
                activeTab={activeTab}
                onTabChanged={(tab) => {
                    if (isMessageTab(tab)) {
                        setActiveTab(tab);
                        setBannerState('idle');
                    }
                }}
            />
            <AssetMessageModalAddContent />
            <MessageBanner state={bannerState} translate={translate} />
        </>
    );
};
